type SentryModule = typeof import('@sentry/angular');

let sentryPromise: Promise<SentryModule> | undefined;

function loadSentry(): Promise<SentryModule> {
  sentryPromise ??= import('@sentry/angular');
  return sentryPromise;
}

export function initializeSentry(
  options: Parameters<SentryModule['init']>[0]
): Promise<void> {
  return loadSentry().then(Sentry => {
    Sentry.init(options);
  });
}

export async function addSentryBrowserIntegrations(): Promise<void> {
  const Sentry = await loadSentry();
  Sentry.addIntegration(Sentry.browserTracingIntegration());
  const replayIntegration =
    await Sentry.lazyLoadIntegration('replayIntegration');
  Sentry.addIntegration(
    replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    })
  );
}

export function addBreadcrumb(
  breadcrumb: Parameters<SentryModule['addBreadcrumb']>[0]
): void {
  void loadSentry().then(Sentry => {
    Sentry.addBreadcrumb(breadcrumb);
  });
}

export function captureException(
  exception: Parameters<SentryModule['captureException']>[0],
  hint?: Parameters<SentryModule['captureException']>[1]
): void {
  void loadSentry().then(Sentry => {
    Sentry.captureException(exception, hint);
  });
}

export function captureExceptionWithExtras(
  exception: Parameters<SentryModule['captureException']>[0],
  extra: Record<string, unknown> | undefined
): void {
  void loadSentry().then(Sentry => {
    Sentry.withScope(scope => {
      if (extra) {
        scope.setExtras(extra);
      }
      Sentry.captureException(exception);
    });
  });
}

export function captureMessage(
  message: Parameters<SentryModule['captureMessage']>[0],
  captureContext?: Parameters<SentryModule['captureMessage']>[1]
): void {
  void loadSentry().then(Sentry => {
    Sentry.captureMessage(message, captureContext);
  });
}

export function flush(
  timeout?: Parameters<SentryModule['flush']>[0]
): Promise<boolean> {
  return loadSentry().then(Sentry => Sentry.flush(timeout));
}

export function setTag(
  key: Parameters<SentryModule['setTag']>[0],
  value: Parameters<SentryModule['setTag']>[1]
): void {
  void loadSentry().then(Sentry => {
    Sentry.setTag(key, value);
  });
}

export function setUser(user: Parameters<SentryModule['setUser']>[0]): void {
  void loadSentry().then(Sentry => {
    Sentry.setUser(user);
  });
}
